import React, { useEffect, useRef, useState } from 'react';
import api from '../api';

// Load the Google Maps JS once for the whole app (shared with Missing Addresses).
let loaderPromise;
export function loadGoogleMaps(key) {
  if (window.google?.maps?.importLibrary) return Promise.resolve();
  if (loaderPromise) return loaderPromise;
  loaderPromise = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}&libraries=places`;
    s.async = true;
    s.onload = resolve;
    s.onerror = reject;
    document.head.appendChild(s);
  });
  return loaderPromise;
}

// Towns we hold local address data for (Overberg). Fetched once per session.
let townsPromise;
export function localTowns() {
  if (!townsPromise) {
    townsPromise = api.get('/geo/towns').then((r) => r.data.map((t) => t.toUpperCase())).catch(() => []);
  }
  return townsPromise;
}
const norm = (s) => String(s || '').toUpperCase().replace(/[^A-Z' ]/g, ' ').replace(/\s+/g, ' ').trim();
export async function isLocalTown(town) {
  if (!town) return false;
  const towns = await localTowns();
  const t = norm(town);
  return towns.some((x) => x === t || x.replace(/[^A-Z]/g, '') === t.replace(/[^A-Z]/g, ''));
}

/**
 * Address input with suggestions. Local Overberg address data first (free,
 * exact street numbers for Gansbaai, Hermanus, Stanford, Bredasdorp…); Google
 * Places (New) only when the town is outside the local data or nothing local
 * matches — Cape Town senders, mostly.
 *
 * Keeps a normal controlled <input> (so free typing + edit prefill work) and
 * renders its own suggestion dropdown. Picking a suggestion calls
 * `onSelect({ address, city, postal_code, latitude, longitude, source })`.
 * `town` is the form's current town, used to scope the local search.
 */
export default function AddressAutocomplete({ value, onChange, onSelect, className, placeholder, town }) {
  const [preds, setPreds] = useState([]);      // [{ kind:'local', row } | { kind:'google', suggestion }]
  const [open, setOpen] = useState(false);
  const libRef = useRef(null);
  const tokenRef = useRef(null);
  const debounceRef = useRef(null);
  const boxRef = useRef(null);
  const seqRef = useRef(0);
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;

  useEffect(() => {
    const key = process.env.REACT_APP_GOOGLE_MAPS_KEY;
    if (!key) return;
    loadGoogleMaps(key)
      .then(() => window.google.maps.importLibrary('places'))
      .then((lib) => { libRef.current = lib; })
      .catch(() => {});
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const googlePreds = async (text) => {
    const lib = libRef.current;
    if (!lib) return [];
    try {
      const { AutocompleteSessionToken, AutocompleteSuggestion } = lib;
      if (!tokenRef.current) tokenRef.current = new AutocompleteSessionToken();
      const { suggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions({
        input: text, sessionToken: tokenRef.current, includedRegionCodes: ['za'],
      });
      return (suggestions || []).map((s) => ({ kind: 'google', suggestion: s }));
    } catch { return []; }
  };

  const fetchPreds = async (text) => {
    if (!text || text.trim().length < 3) { setPreds([]); setOpen(false); return; }
    const seq = ++seqRef.current;
    let list = [];
    // Local first — unless the form's town is known and is not one we have data for
    const townIsElsewhere = town ? !(await isLocalTown(town)) : false;
    if (!townIsElsewhere) {
      try {
        const { data } = await api.get('/geo/suggest', { params: { q: text, town: town || '' } });
        list = (data || []).map((row) => ({ kind: 'local', row }));
      } catch { list = []; }
    }
    if (!list.length) list = await googlePreds(text);
    // Local matches can be near-misses (or the address is outside the Overberg):
    // offer Google as one explicit click rather than a call on every keystroke.
    else if (libRef.current) list = [...list, { kind: 'google-search', text }];
    if (seq !== seqRef.current) return;   // a newer keystroke won
    setPreds(list);
    setOpen(list.length > 0);
  };

  const searchGoogle = async (text) => {
    const list = await googlePreds(text);
    setPreds(list.length ? list : [{ kind: 'google-none', text }]);
    setOpen(true);
  };

  const handleChange = (e) => {
    const text = e.target.value;
    onChange(text);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchPreds(text), 250);
  };

  const choose = async (p) => {
    if (p.kind === 'google-search') { await searchGoogle(p.text); return; }
    if (p.kind === 'google-none') { setOpen(false); setPreds([]); return; }
    setOpen(false);
    setPreds([]);
    if (p.kind === 'local') {
      const r = p.row;
      onSelectRef.current({
        address: r.address, city: r.town || '', postal_code: r.postcode || '',
        latitude: r.lat, longitude: r.lng, source: 'local', suburb: r.suburb || '',
      });
      return;
    }
    try {
      const place = p.suggestion.placePrediction.toPlace();
      await place.fetchFields({ fields: ['formattedAddress', 'location', 'addressComponents'] });
      tokenRef.current = null; // session complete
      const comp = place.addressComponents || [];
      const get = (t) => comp.find((c) => c.types.includes(t))?.longText;
      onSelectRef.current({
        address: place.formattedAddress || '',
        city: get('locality') || get('administrative_area_level_2') || get('administrative_area_level_1') || '',
        postal_code: get('postal_code') || '',
        latitude: place.location.lat(),
        longitude: place.location.lng(),
        source: 'google',
      });
    } catch {
      /* ignore — keep typed text */
    }
  };

  return (
    <div ref={boxRef} className="relative">
      <input
        type="text"
        value={value || ''}
        onChange={handleChange}
        onFocus={() => { if (preds.length) setOpen(true); }}
        placeholder={placeholder}
        className={className}
        autoComplete="off"
      />
      {open && preds.length > 0 && (
        <ul className="absolute z-50 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-auto">
          {preds.map((p, i) => (
            p.kind === 'google-search' ? (
              <li key={i} onMouseDown={(e) => { e.preventDefault(); choose(p); }}
                className="px-3 py-2 text-xs text-blue-700 hover:bg-blue-50 cursor-pointer border-t border-gray-100">
                🔍 Not here? Search Google Maps for “{p.text}”
              </li>
            ) : p.kind === 'google-none' ? (
              <li key={i} onMouseDown={(e) => { e.preventDefault(); choose(p); }} className="px-3 py-2 text-xs text-gray-500">
                Google found nothing for “{p.text}” — drop a pin on the map instead
              </li>
            ) : (
            <li
              key={i}
              onMouseDown={(e) => { e.preventDefault(); choose(p); }}
              className="px-3 py-2 text-sm text-gray-700 hover:bg-orange-50 cursor-pointer flex items-center justify-between gap-2"
            >
              <span>{p.kind === 'local' ? p.row.label : (p.suggestion.placePrediction?.text?.text || '')}</span>
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full flex-shrink-0 ${p.kind === 'local' ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                {p.kind === 'local' ? 'local' : 'Google'}
              </span>
            </li>
            )
          ))}
        </ul>
      )}
    </div>
  );
}
